import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Loader2,
  RefreshCw,
  ShieldAlert,
  Globe2,
  FileWarning,
  CheckCircle2,
  Eye,
  Filter,
  Activity,
  Database,
  AlertTriangle,
  Download,
  PlayCircle,
} from 'lucide-react';
import AdminStatusStrip from '../components/AdminStatusStrip';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_BADGE = {
  critical: { bg: '#7F1D1D', fg: '#FECACA', label: 'CRITICAL' },
  high:     { bg: '#9A3412', fg: '#FED7AA', label: 'HIGH' },
  medium:   { bg: '#854D0E', fg: '#FEF08A', label: 'MEDIUM' },
  low:      { bg: '#1E3A5F', fg: '#BFDBFE', label: 'LOW' },
};

const formatDateTime = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('pl-PL', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) { return s; }
};

const SeverityBadge = ({ severity }) => {
  const cfg = SEVERITY_BADGE[severity] || SEVERITY_BADGE.low;
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
        background: cfg.bg, color: cfg.fg,
      }}
      data-testid={`intel-severity-${severity}`}
    >
      {cfg.label}
    </span>
  );
};

const UpdateCard = ({ update, onReview }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'pl';
  const title = (lang === 'en' ? update.title_en : update.title_pl) || update.title_en || update.title_pl;
  const summary = (lang === 'en' ? update.summary_en : update.summary_pl) || '';
  const disclaimer = (lang === 'en' ? update.disclaimer_en : update.disclaimer_pl);
  return (
    <Card
      className="hussar-tc-card"
      style={{ padding: 18, marginBottom: 16 }}
      data-testid={`intel-update-card-${update.id}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <SeverityBadge severity={update.severity} />
            <span style={{ fontSize: 11, opacity: 0.7, letterSpacing: 0.5 }}>
              <Globe2 className="w-3 h-3 inline-block mr-1" />
              {update.jurisdiction}
            </span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>· {update.category}</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>· {formatDateTime(update.detected_at)}</span>
          </div>
          <h3 style={{ margin: '8px 0 4px', fontSize: 18, fontWeight: 600 }}>
            {title || t('intel.untitled')}
          </h3>
          <p style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9, margin: 0 }}>
            {summary}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          {update.human_review_required && (
            <span
              style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 4,
                background: update.review_status === 'reviewed' ? '#064E3B' : '#7C2D12',
                color: update.review_status === 'reviewed' ? '#A7F3D0' : '#FED7AA',
                fontWeight: 600,
              }}
              data-testid={`intel-review-status-${update.id}`}
            >
              {update.review_status === 'reviewed' ? t('intel.reviewed') : t('intel.pendingReview')}
            </span>
          )}
          {update.review_status !== 'reviewed' && update.human_review_required && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReview(update.id, 'reviewed')}
              data-testid={`intel-mark-reviewed-${update.id}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t('intel.markReviewed')}
            </Button>
          )}
        </div>
      </div>

      {update.what_changed && (
        <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
          <strong>{t('intel.whatChanged')}: </strong>{update.what_changed}
        </div>
      )}
      {update.possible_impact && (
        <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
          <strong>{t('intel.possibleImpact')}: </strong>{update.possible_impact}
        </div>
      )}
      {(update.forms_affected?.length > 0 || update.deadlines_affected?.length > 0 || update.fees_affected?.length > 0) && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          {update.forms_affected?.length > 0 && (
            <div><strong>{t('intel.formsAffected')}:</strong> {update.forms_affected.join(', ')}</div>
          )}
          {update.deadlines_affected?.length > 0 && (
            <div><strong>{t('intel.deadlinesAffected')}:</strong> {update.deadlines_affected.join(', ')}</div>
          )}
          {update.fees_affected?.length > 0 && (
            <div><strong>{t('intel.feesAffected')}:</strong> {update.fees_affected.join(', ')}</div>
          )}
        </div>
      )}
      {update.citations?.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <strong>{t('intel.citations')}:</strong>{' '}
          {update.citations.map((c, i) => (
            <a
              key={c}
              href={c}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: '#7DD3FC', marginRight: 8, wordBreak: 'break-all' }}
              data-testid={`intel-citation-${update.id}-${i}`}
            >
              {c.length > 60 ? `${c.slice(0, 60)}…` : c}
            </a>
          ))}
        </div>
      )}
      <div
        style={{
          marginTop: 12, padding: '6px 10px', fontSize: 11, opacity: 0.7,
          background: 'rgba(255,255,255,0.04)', borderRadius: 4,
          fontStyle: 'italic',
        }}
        data-testid={`intel-disclaimer-${update.id}`}
      >
        {disclaimer}
      </div>
    </Card>
  );
};

const ImmigrationIntel = () => {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filters, setFilters] = useState({
    jurisdiction: 'all',
    severity: 'all',
    review_status: 'all',
    q: '',
  });
  const [total, setTotal] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/intel/summary`, { withCredentials: true });
      setSummary(r.data);
    } catch (e) { /* anonymous → AdminRoute redirects elsewhere */ }
  }, []);

  const loadUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.jurisdiction !== 'all') params.set('jurisdiction', filters.jurisdiction);
      if (filters.severity !== 'all') params.set('severity', filters.severity);
      if (filters.review_status !== 'all') params.set('review_status', filters.review_status);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', '100');
      const r = await axios.get(`${API}/admin/intel/updates?${params.toString()}`, { withCredentials: true });
      setUpdates(r.data.updates || []);
      setTotal(r.data.total || 0);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadUpdates(); }, [loadUpdates]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/intel/run-now`, {}, { withCredentials: true });
      await loadSummary();
      await loadUpdates();
    } catch (e) { /* noop */ }
    finally { setRunning(false); }
  };

  const handleMarkReviewed = async (id, status) => {
    try {
      await axios.post(
        `${API}/admin/intel/updates/${id}/review`,
        { status },
        { withCredentials: true },
      );
      await loadSummary();
      await loadUpdates();
    } catch (e) { /* noop */ }
  };

  const handleExportBrief = async () => {
    try {
      const r = await axios.get(`${API}/admin/intel/export-brief?severity=high`, { withCredentials: true });
      const blob = new Blob([r.data.text || ''], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hussarai-intel-brief-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const lang = i18n.language || 'pl';
  const pageTitle = lang === 'en'
    ? 'Immigration & Bureaucracy Intelligence Center'
    : 'Centrum zmian: prawo, urzędy i imigracja';
  const lastRunLabel = summary?.lastRun ? formatDateTime(summary.lastRun.started_at) : t('intel.never');

  const stats = useMemo(() => ([
    { key: 'sources',  icon: Database,    value: summary?.enabledSources ?? '—', label: t('intel.statSources') },
    { key: 'updates',  icon: Activity,    value: summary?.totalUpdates ?? '—',   label: t('intel.statUpdates') },
    { key: 'review',   icon: Eye,         value: summary?.pendingReview ?? '—',  label: t('intel.statPendingReview') },
    { key: 'critical', icon: AlertTriangle, value: summary?.highOrCritical ?? '—', label: t('intel.statHighCritical') },
  ]), [summary, t]);

  return (
    <div className="hussar-landing" data-testid="immigration-intel-page" style={{ minHeight: '100vh' }}>
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 80px' }}>
        <AdminStatusStrip />
        <header style={{ margin: '12px 0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7, fontSize: 11, letterSpacing: 0.5 }}>
            <ShieldAlert className="w-3 h-3" /> HUSSARAI · INTEL · DAILY MONITOR
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 600, margin: '8px 0 4px' }} data-testid="intel-page-title">
            {pageTitle}
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>
            {t('intel.subtitle')}
          </p>
          <p style={{ fontSize: 12, opacity: 0.6, marginTop: 6, fontStyle: 'italic' }} data-testid="intel-doctrine">
            {t('intel.doctrineLine')}
          </p>
        </header>

        <Card style={{ padding: 16, marginBottom: 24 }} data-testid="intel-overview">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {t('intel.lastRun')}: <strong data-testid="intel-last-run">{lastRunLabel}</strong>
              </div>
              {summary?.lastRun && (
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                  {t('intel.sourcesChecked')}: {summary.lastRun.sources_checked} ·
                  {' '}{t('intel.changesDetected')}: {summary.lastRun.changes_detected} ·
                  {' '}{t('intel.failures')}: {summary.lastRun.failures}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { loadSummary(); loadUpdates(); }}
                data-testid="intel-refresh-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> {t('intel.refresh')}
              </Button>
              <Button
                size="sm"
                onClick={handleRunNow}
                disabled={running}
                data-testid="intel-run-now-btn"
                className="hussar-btn-primary"
              >
                {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                {running ? t('intel.running') : t('intel.runNow')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportBrief}
                data-testid="intel-export-brief-btn"
              >
                <Download className="w-3 h-3 mr-1" /> {t('intel.exportBrief')}
              </Button>
            </div>
          </div>
          <div
            style={{
              marginTop: 16, display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12,
            }}
            data-testid="intel-stats-grid"
          >
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  style={{ padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}
                  data-testid={`intel-stat-${s.key}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7, fontSize: 11 }}>
                    <Icon className="w-3 h-3" /> {s.label}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{s.value}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card style={{ padding: 16, marginBottom: 16 }} data-testid="intel-filters">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 12, opacity: 0.7 }}>
            <Filter className="w-3 h-3" /> {t('intel.filters')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            <Select value={filters.jurisdiction} onValueChange={(v) => setFilters((f) => ({ ...f, jurisdiction: v }))}>
              <SelectTrigger data-testid="intel-filter-jurisdiction"><SelectValue placeholder={t('intel.allJurisdictions')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('intel.allJurisdictions')}</SelectItem>
                <SelectItem value="Poland">Poland</SelectItem>
                <SelectItem value="European Union">European Union</SelectItem>
                <SelectItem value="Ireland">Ireland</SelectItem>
                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                <SelectItem value="Germany">Germany</SelectItem>
                <SelectItem value="Czechia">Czechia</SelectItem>
                <SelectItem value="Slovakia">Slovakia</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(v) => setFilters((f) => ({ ...f, severity: v }))}>
              <SelectTrigger data-testid="intel-filter-severity"><SelectValue placeholder={t('intel.allSeverities')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('intel.allSeverities')}</SelectItem>
                <SelectItem value="critical">critical</SelectItem>
                <SelectItem value="high">high</SelectItem>
                <SelectItem value="medium">medium</SelectItem>
                <SelectItem value="low">low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.review_status} onValueChange={(v) => setFilters((f) => ({ ...f, review_status: v }))}>
              <SelectTrigger data-testid="intel-filter-review"><SelectValue placeholder={t('intel.allReviewStatuses')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('intel.allReviewStatuses')}</SelectItem>
                <SelectItem value="pending">{t('intel.pendingReview')}</SelectItem>
                <SelectItem value="reviewed">{t('intel.reviewed')}</SelectItem>
                <SelectItem value="dismissed">{t('intel.dismissed')}</SelectItem>
                <SelectItem value="not_required">{t('intel.notRequired')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder={t('intel.searchPlaceholder')}
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              data-testid="intel-filter-search"
            />
          </div>
        </Card>

        <div data-testid="intel-updates-list">
          {loading ? (
            <Card style={{ padding: 24, textAlign: 'center' }}>
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t('intel.loading')}
            </Card>
          ) : updates.length === 0 ? (
            <Card style={{ padding: 24, textAlign: 'center' }} data-testid="intel-empty">
              <FileWarning className="w-5 h-5 inline-block mr-2 opacity-60" />
              {t('intel.empty')}
            </Card>
          ) : (
            <>
              <div style={{ fontSize: 12, opacity: 0.6, margin: '6px 0 10px' }}>
                {t('intel.showing')}: {updates.length} / {total}
              </div>
              {updates.map((u) => (
                <UpdateCard key={u.id} update={u} onReview={handleMarkReviewed} />
              ))}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ImmigrationIntel;
